/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { PiCode, PiTextStrikethrough, PiTextUnderline } from "react-icons/pi";

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/Elements";
import { cn } from "@/utils/style";
import { $createCodeNode, $isCodeHighlightNode } from "@lexical/code";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $createListNode, $isListNode, ListNode } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
} from "@lexical/rich-text";
import { $patchStyleText, $setBlocksType } from "@lexical/selection";
import {
  $findMatchingParent,
  $getNearestNodeOfType,
  mergeRegister,
} from "@lexical/utils";
import { CaretDownIcon } from "@radix-ui/react-icons";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  KEY_DOWN_COMMAND,
  LexicalCommand,
  LexicalEditor,
  NodeKey,
  SELECTION_CHANGE_COMMAND,
  createCommand,
} from "lexical";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ImLink } from "react-icons/im";
import {
  TbAlignCenter,
  TbAlignLeft,
  TbAlignRight,
  TbBold,
  TbItalic,
  TbSparkles,
} from "react-icons/tb";

import { IS_APPLE } from "../../shared/environment";
import { getDOMRangeRect } from "../../utils/getDOMRangeRect";
import { getSelectedNode } from "../../utils/getSelectedNode";
import { setFloatingElemPosition } from "../../utils/setFloatingElemPosition";
import {
  ComponentPickerMenuItem,
  ComponentPickerOption,
  FormattingIconMap,
} from "../ComponentPickerPlugin";

export const INSERT_AI_AUTOCOMPLETE_COMMAND: LexicalCommand<string> =
  createCommand();

const rootTypeToRootName = {
  root: "Root",
  table: "Table",
};

const blockTypeToBlockName = {
  bullet: "Bulleted List",
  check: "Check List",
  code: "Code Block",
  h1: "Heading 1",
  h2: "Heading 2",
  h3: "Heading 3",
  h4: "Heading 4",
  h5: "Heading 5",
  h6: "Heading 6",
  number: "Numbered List",
  paragraph: "Text",
  quote: "Quote",
};

const blockTypeToBlockIcon = {
  bullet: FormattingIconMap["Bulleted List"],
  check: FormattingIconMap["Check List"],
  code: FormattingIconMap["Code Block"],
  h1: FormattingIconMap["Heading 1"],
  h2: FormattingIconMap["Heading 2"],
  h3: FormattingIconMap["Heading 3"],
  h4: FormattingIconMap["Heading 4"],
  h5: FormattingIconMap["Heading 5"],
  h6: FormattingIconMap["Heading 6"],
  number: FormattingIconMap["Numbered List"],
  text: FormattingIconMap["Text"],
  quote: FormattingIconMap["Quote"],
};

type Props = {
  disabled?: boolean;
  buttonAriaLabel?: string;
  buttonClassName: string;
  buttonIconClassName?: string;
  buttonLabel?: string;
  title?: string;
  stopCloseOnClickSelf?: boolean;
  color: string;
  onChange?: (color: string, skipHistoryStack: boolean) => void;
  buttonIcon: React.ReactNode;
  tooltipContent?: string;
  handleResetColor?: () => void;
};

import ColorPicker from "@/components/Elements/ColorPicker/ColorPicker";
import { HighlighterIcon, LucideRemoveFormatting } from "lucide-react";
import DropDown from "../../ui/Dropdown";

function DropdownColorPicker({
  disabled = false,
  stopCloseOnClickSelf = true,
  color,
  onChange,
  buttonIcon,
  buttonIconClassName,
  tooltipContent,
  handleResetColor,
  ...rest
}: Props) {
  return (
    <DropDown
      {...rest}
      disabled={disabled}
      stopCloseOnClickSelf={stopCloseOnClickSelf}
      buttonIcon={buttonIcon}
      buttonTooltipContent={tooltipContent}
      buttonIconClassName={buttonIconClassName}
      color={color}
    >
      <ColorPicker
        color={color}
        onChange={onChange}
        handleResetColor={handleResetColor}
      />
    </DropDown>
  );
}

// Function to generate component picker options
export const createComponentPickerOptions = (editor) => ({
  Text: new ComponentPickerOption("Text", {
    icon: FormattingIconMap.Text,
    keywords: ["normal", "paragraph", "p", "text"],
    onSelect: () =>
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      }),
  }),
  "Heading 1": new ComponentPickerOption(`Heading 1`, {
    icon: FormattingIconMap[`Heading 1`],
    keywords: ["heading", "header", `h1`],
    onSelect: () =>
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () =>
            // @ts-ignore Correct types, but since they're dynamic TS doesn't like it.
            $createHeadingNode(`h1`)
          );
        }
      }),
  }),
  "Heading 2": new ComponentPickerOption(`Heading 2`, {
    icon: FormattingIconMap[`Heading 2`],
    keywords: ["heading", "header", `h2`],
    onSelect: () =>
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () =>
            // @ts-ignore Correct types, but since they're dynamic TS doesn't like it.
            $createHeadingNode(`h2`)
          );
        }
      }),
  }),
  "Heading 3": new ComponentPickerOption(`Heading 3`, {
    icon: FormattingIconMap[`Heading 3`],
    keywords: ["heading", "header", `h3`],
    onSelect: () =>
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () =>
            // @ts-ignore Correct types, but since they're dynamic TS doesn't like it.
            $createHeadingNode(`h3`)
          );
        }
      }),
  }),
  "Heading 4": new ComponentPickerOption(`Heading 4`, {
    icon: FormattingIconMap[`Heading 4`],
    keywords: ["heading", "header", `h4`],
    onSelect: () =>
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () =>
            // @ts-ignore Correct types, but since they're dynamic TS doesn't like it.
            $createHeadingNode(`h4`)
          );
        }
      }),
  }),
  Quote: new ComponentPickerOption("Quote", {
    icon: FormattingIconMap.Quote,
    keywords: ["quote", "blockquote"],
    onSelect: () =>
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      }),
  }),
  "Numbered List": new ComponentPickerOption("Numbered List", {
    icon: FormattingIconMap["Numbered List"],
    keywords: ["numbered list", "ordered list", "ol"],
    onSelect: () =>
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createListNode("number", 1));
        }
      }),
  }),
  "Bulleted List": new ComponentPickerOption("Bulleted List", {
    icon: FormattingIconMap["Bulleted List"],
    keywords: ["bulleted list", "unordered list", "ul"],
    onSelect: () =>
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createListNode("bullet", 1));
        }
      }),
  }),
  "Check List": new ComponentPickerOption("Check List", {
    icon: FormattingIconMap["Check List"],
    keywords: ["check list", "todo list"],
    onSelect: () =>
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createListNode("check", 1));
        }
      }),
  }),
  "Code Block": new ComponentPickerOption("Code Block", {
    icon: FormattingIconMap["Code Block"],
    keywords: ["code", "code block"],
    onSelect: () =>
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createCodeNode());
        }
      }),
  }),
});

function TextFormatFloatingToolbar({
  editor,
  anchorElem,
  isLink,
  isBold,
  isItalic,
  isUnderline,
  isCode,
  isStrikethrough,
  selectedElement,
  setSelectedElement,
  setIsLinkEditMode,
  blockType,
  isTemplate,
}: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  isLink: boolean;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isStrikethrough: boolean;
  isCode: boolean;
  selectedElement: ComponentPickerOption | null;
  setSelectedElement: (selectedElement: ComponentPickerOption | null) => void;
  setIsLinkEditMode: (isLinkEditMode: boolean) => void;
  blockType: keyof typeof blockTypeToBlockName;
  isTemplate?: boolean;
}): JSX.Element {
  const popupCharStylesEditorRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);

  const [activeEditor, setActiveEditor] = useState(editor);

  const [rootType, setRootType] =
    useState<keyof typeof rootTypeToRootName>("root");
  const [selectedElementKey, setSelectedElementKey] = useState<NodeKey | null>(
    null
  );
  const [fontColor, setFontColor] = useState<string>("#000");
  const [bgColor, setBgColor] = useState<string>("#fff");
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());

  const componentPickerOptions = createComponentPickerOptions(editor);

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
      setIsLinkEditMode(true);
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  function mouseMoveListener(e: MouseEvent) {
    if (
      popupCharStylesEditorRef?.current &&
      (e.buttons === 1 || e.buttons === 3)
    ) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== "none") {
        const x = e.clientX;
        const y = e.clientY;
        const elementUnderMouse = document.elementFromPoint(x, y);

        if (!popupCharStylesEditorRef.current.contains(elementUnderMouse)) {
          // Mouse is not over the target element => not a normal click, but probably a drag
          popupCharStylesEditorRef.current.style.pointerEvents = "none";
        }
      }
    }
  }
  function mouseUpListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== "auto") {
        popupCharStylesEditorRef.current.style.pointerEvents = "auto";
      }
    }
  }

  useEffect(() => {
    if (popupCharStylesEditorRef?.current) {
      document.addEventListener("mousemove", mouseMoveListener);
      document.addEventListener("mouseup", mouseUpListener);

      return () => {
        document.removeEventListener("mousemove", mouseMoveListener);
        document.removeEventListener("mouseup", mouseUpListener);
      };
    }
  }, [popupCharStylesEditorRef]);

  const updateTextFormatFloatingToolbar = useCallback(() => {
    const selection = $getSelection();

    const popupCharStylesEditorElem = popupCharStylesEditorRef.current;
    const nativeSelection = window.getSelection();

    if (popupCharStylesEditorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const rangeRect = getDOMRangeRect(nativeSelection, rootElement);

      setFloatingElemPosition(
        rangeRect,
        popupCharStylesEditorElem,
        anchorElem,
        8
      );
    }
  }, [editor, anchorElem, activeEditor, open]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        updateTextFormatFloatingToolbar();
      });
    };

    window.addEventListener("resize", update);
    if (scrollerElem) {
      scrollerElem.addEventListener("scroll", update);
    }

    return () => {
      window.removeEventListener("resize", update);
      if (scrollerElem) {
        scrollerElem.removeEventListener("scroll", update);
      }
    };
  }, [editor, updateTextFormatFloatingToolbar, anchorElem]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateTextFormatFloatingToolbar();
    });
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateTextFormatFloatingToolbar();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateTextFormatFloatingToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, updateTextFormatFloatingToolbar]);

  const applyStyleText = useCallback(
    (styles: Record<string, string>, skipHistoryStack?: boolean) => {
      activeEditor.update(
        () => {
          const selection = $getSelection();
          if (selection !== null) {
            $patchStyleText(selection, styles);
          }
        },
        skipHistoryStack ? { tag: "historic" } : {}
      );
    },
    [activeEditor]
  );

  const onFontColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean) => {
      applyStyleText({ color: value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  const onBgColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean) => {
      applyStyleText({ "background-color": value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  const handleResetColor = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.getNodes().forEach((node) => {
          if ($isTextNode(node)) {
            node.setStyle("");
          } else if ($isLinkNode(node)) {
            const children = node.getChildren();
            for (const child of children) {
              node.insertBefore(child);
            }
            node.remove();
          }
        });
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  return (
    <div
      ref={popupCharStylesEditorRef}
      className="flex items-center align-middle absolute top-0 left-0 h-10 z-[9999999] origin-top-right divide-y divide-zinc-100 dark:divide-zinc-600 rounded-md backdrop-blur-lg bg-white/90 dark:bg-zinc-800 shadow-md dark:shadow-lg ring-1 ring-zinc-900 dark:ring-1 dark:ring-zinc-50 dark:ring-opacity-15 ring-opacity-5 focus:outline-none transition-opacity"
    >
      {editor.isEditable() && (
        <div className="first:ml-1 last:mr-1 flex items-center">
          {!isTemplate && (
            <>
              <Button
                variant="text"
                className="font-normal !px-2"
                textClassName="text-sm font-semibold text-emerald-600 dark:text-emerald-500 !px-0 whitespace-nowrap"
                startIcon={
                  <TbSparkles
                    className="text-emerald-600 dark:text-emerald-500 w-4 h-4"
                    style={{
                      fill: "#059669",
                      strokeColor: "#059669",
                    }}
                  />
                }
                tooltipContent="Tell AI what to edit or rewrite"
                tooltipModifierKey={IS_APPLE ? "⌘+J" : "Ctrl+J"}
                tooltipDirection="top"
                onClick={() => {
                  const event = new KeyboardEvent("keydown", {
                    key: "j",
                    ctrlKey: true,
                    bubbles: true,
                  });
                  editor.dispatchCommand(KEY_DOWN_COMMAND, event);
                }}
              >
                AI Edit
              </Button>
              <div className="flex flex-col justify-center mx-1 h-6 bg-zinc-200 dark:bg-zinc-700 w-px"></div>
            </>
          )}
          {blockType in blockTypeToBlockName && (
            <DropdownMenu open={open} onOpenChange={setOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="text"
                  className="font-normal !px-2"
                  textClassName="text-sm font-base text-zinc-900 dark:text-white !px-0 whitespace-nowrap"
                  endIcon={<CaretDownIcon />}
                  tooltipContent="Select element"
                  tooltipDirection="top"
                >
                  {blockTypeToBlockName[blockType]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  {Object.values(componentPickerOptions).map((option, i) => (
                    <ComponentPickerMenuItem
                      index={i}
                      isSelected={selectedElement === option}
                      onClick={() => {
                        setSelectedElement(option);
                      }}
                      key={option.key}
                      option={option}
                    />
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <div className="flex flex-col justify-center mx-1 h-6 bg-zinc-200 dark:bg-zinc-700 w-px"></div>
          <Button
            variant="icon"
            size="md"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
            }}
            buttonIcon={
              <TbBold
                style={{
                  strokeWidth: 2,
                }}
                className="text-zinc-700 dark:text-white "
              />
            }
            className={cn(
              "py-2 px-2",
              isBold && " bg-zinc-200/50 dark:bg-zinc-800 "
            )}
            tooltipContent="Bold"
            tooltipModifierKey={IS_APPLE ? "⌘B" : "Ctrl+B"}
            tooltipDirection="top"
            aria-label={`Format text as bold. Shortcut: ${
              IS_APPLE ? "⌘B" : "Ctrl+B"
            }`}
          ></Button>
          <Button
            variant="icon"
            size="md"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
            }}
            className={cn(
              "py-2 px-2",
              isItalic ? "bg-zinc-200/50 dark:bg-zinc-800" : ""
            )}
            tooltipContent="Italic"
            tooltipModifierKey={IS_APPLE ? "⌘I" : "Ctrl+I"}
            tooltipDirection="top"
            type="button"
            buttonIcon={
              <TbItalic
                style={{
                  strokeWidth: 2,
                }}
              />
            }
            aria-label={`Format text as italics. Shortcut: ${
              IS_APPLE ? "⌘I" : "Ctrl+I"
            }`}
          ></Button>
          <Button
            variant="icon"
            size="md"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
            }}
            className={cn(
              "py-2 px-2",
              isUnderline ? "bg-zinc-200/50 dark:bg-zinc-800" : ""
            )}
            textClassName="underline"
            buttonIcon={
              <PiTextUnderline
                style={{
                  strokeWidth: 5,
                }}
              />
            }
            tooltipContent="Underline"
            tooltipModifierKey={IS_APPLE ? "⌘U" : "Ctrl+U"}
            tooltipDirection="top"
            aria-label={`Format text to underlined. Shortcut: ${
              IS_APPLE ? "⌘U" : "Ctrl+U"
            }`}
          ></Button>
          <Button
            variant="icon"
            size="md"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
            }}
            className={cn(
              "py-2 px-2",
              isStrikethrough ? "bg-zinc-200/50 dark:bg-zinc-800" : ""
            )}
            textClassName="strikethrough"
            buttonIcon={
              <PiTextStrikethrough
                style={{
                  strokeWidth: 5,
                }}
              />
            }
            aria-label="Format text with a strikethrough"
            tooltipContent="Strikethrough"
            tooltipDirection="top"
          ></Button>
          <Button
            variant="icon"
            size="md"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
            }}
            className={cn(
              "py-2 px-2",
              isCode ? "bg-zinc-200/50 dark:bg-zinc-800" : ""
            )}
            aria-label="Insert code block"
            buttonIcon={
              <PiCode
                style={{
                  strokeWidth: 5,
                }}
              />
            }
            tooltipContent="Insert code block"
            tooltipDirection="top"
          ></Button>
          <Button
            variant="icon"
            size="md"
            onClick={insertLink}
            className={cn(
              "py-2 px-2",
              isLink ? "bg-zinc-200/50 dark:bg-zinc-800" : ""
            )}
            aria-label="Insert link"
            buttonIcon={
              <ImLink
                style={{
                  width: "0.8rem",
                  height: "0.8rem",
                }}
              />
            }
            tooltipContent="Insert link"
            tooltipDirection="top"
          ></Button>
          <div className="flex flex-col justify-center mx-1 h-6 bg-zinc-200 dark:bg-zinc-700 w-px"></div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="icon"
                size="md"
                aria-label="Text alignment"
                className={cn("py-2 px-2")}
                buttonIcon={
                  <TbAlignLeft
                    style={{
                      strokeWidth: 2,
                    }}
                  />
                }
                tooltipContent="Text alignment"
                tooltipDirection="top"
                endIcon={<CaretDownIcon />}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuGroup>
                <DropdownMenuItem
                  tabIndex={0}
                  onClick={() => {
                    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
                  }}
                  className="flex items-center gap-2"
                >
                  <TbAlignLeft
                    style={{
                      strokeWidth: 2,
                      height: "1rem",
                      width: "1rem",
                    }}
                  />
                  Align left
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center");
                  }}
                  className="flex items-center gap-2"
                >
                  <TbAlignCenter
                    style={{
                      strokeWidth: 2,
                      height: "1rem",
                      width: "1rem",
                    }}
                  />
                  Align center
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right");
                  }}
                  className="flex items-center gap-2"
                >
                  <TbAlignRight
                    style={{
                      strokeWidth: 2,
                      height: "1rem",
                      width: "1rem",
                    }}
                  />
                  Align right
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex flex-col justify-center mx-1 h-6 bg-zinc-200 dark:bg-zinc-700 w-px"></div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="icon"
                disabled={!isEditable}
                aria-label="Formatting text color"
                className={"py-2 px-2"}
                tooltipContent="Text color"
                tooltipDirection="top"
                buttonIcon={
                  <div
                    className="w-4 h-4"
                    style={{
                      borderRadius: "50%",
                      backgroundColor: fontColor,
                      border: "1px solid rgba(212, 212, 216, 1)",
                    }}
                  />
                }
              ></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <ColorPicker
                color={fontColor}
                onChange={onFontColorSelect}
                handleResetColor={handleResetColor}
              />
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="icon"
                disabled={!isEditable}
                aria-label="Formatting background color"
                className={"py-2 px-2"}
                tooltipContent="Background color"
                tooltipDirection="top"
                buttonIcon={
                  <div
                    className="flex items-center justify-center"
                    style={{
                      position: "relative",
                    }}
                  >
                    <HighlighterIcon className="w-4 h-4 font-normal text-sm absolute bottom-[0px] left-0 right-0">
                      A
                    </HighlighterIcon>
                    <div
                      style={{
                        position: "absolute",
                        bottom: "-4px",
                        left: "-2px",
                        right: "-2px",
                        borderRadius: "2px",
                        height: "3.5px",
                        backgroundColor: bgColor,
                      }}
                    />
                  </div>
                }
              ></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <ColorPicker
                color={bgColor}
                onChange={onBgColorSelect}
                handleResetColor={handleResetColor}
              />
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex flex-col justify-center mx-1 h-6 bg-zinc-200 dark:bg-zinc-700 w-px"></div>

          <Button
            variant="icon"
            size="md"
            className="py-2 px-2"
            buttonIcon={<LucideRemoveFormatting />}
            tooltipContent="Clear formatting"
            tooltipDirection="top"
            onClick={() => {
              editor.update(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                  selection.getNodes().forEach((node) => {
                    if ($isTextNode(node)) {
                      node.setFormat(0);
                      node.setStyle("");
                    } else if ($isLinkNode(node)) {
                      const children = node.getChildren();
                      for (const child of children) {
                        node.insertBefore(child);
                      }
                      node.remove();
                    }
                  });
                  $setBlocksType(selection, () => $createParagraphNode());
                }
              });
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
            }}
            aria-label="Clear formatting"
          ></Button>
        </div>
      )}
    </div>
  );
}

function useFloatingTextFormatToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  setIsLinkEditMode: (isLinkEditMode: boolean) => void,
  isTemplate?: boolean
): JSX.Element | null {
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isText, setIsText] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [selectedElement, setSelectedElement] =
    useState<ComponentPickerOption | null>(null);
  const [blockType, setBlockType] =
    useState<keyof typeof blockTypeToBlockName>("paragraph");

  const updatePopup = useCallback(() => {
    editor.getEditorState().read(() => {
      // Should not to pop up the floating toolbar when using IME input
      if (editor.isComposing()) {
        return;
      }
      const selection = $getSelection();
      const nativeSelection = window.getSelection();
      const rootElement = editor.getRootElement();

      if (
        nativeSelection !== null &&
        (!$isRangeSelection(selection) ||
          rootElement === null ||
          !rootElement.contains(nativeSelection.anchorNode))
      ) {
        setIsText(false);
        return;
      }

      if (!$isRangeSelection(selection)) {
        return;
      }

      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const node = getSelectedNode(selection);
      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      setIsSubscript(selection.hasFormat("subscript"));
      setIsSuperscript(selection.hasFormat("superscript"));
      setIsCode(selection.hasFormat("code"));

      // Update links
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(
            anchorNode,
            ListNode
          );
          const type = parentList
            ? parentList.getListType()
            : element.getListType();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          if (type in blockTypeToBlockName) {
            setBlockType(type as keyof typeof blockTypeToBlockName);
          }
        }
      }

      if (
        !$isCodeHighlightNode(selection.anchor.getNode()) &&
        selection.getTextContent() !== ""
      ) {
        setIsText($isTextNode(node));
      } else {
        setIsText(false);
      }

      const rawTextContent = selection.getTextContent().replace(/\n/g, "");
      if (!selection.isCollapsed() && rawTextContent === "") {
        setIsText(false);
        return;
      }
    });
  }, [editor]);

  useEffect(() => {
    document.addEventListener("selectionchange", updatePopup);
    return () => {
      document.removeEventListener("selectionchange", updatePopup);
    };
  }, [updatePopup]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updatePopup();
      }),
      editor.registerRootListener(() => {
        if (editor.getRootElement() === null) {
          setIsText(false);
        }
      })
    );
  }, [editor, updatePopup]);

  useEffect(() => {
    if (selectedElement) {
      selectedElement.onSelect(editor);
    }
  }, [editor, selectedElement]);

  if (!isText) {
    return null;
  }

  return createPortal(
    <TextFormatFloatingToolbar
      editor={editor}
      anchorElem={anchorElem}
      selectedElement={selectedElement}
      setSelectedElement={setSelectedElement}
      isLink={isLink}
      isBold={isBold}
      isItalic={isItalic}
      isStrikethrough={isStrikethrough}
      isSubscript={isSubscript}
      isSuperscript={isSuperscript}
      isUnderline={isUnderline}
      isCode={isCode}
      blockType={blockType}
      setIsLinkEditMode={setIsLinkEditMode}
      isTemplate={isTemplate}
    />,
    anchorElem
  );
}

export default function FloatingTextFormatToolbarPlugin({
  anchorElem = document.body,
  setIsLinkEditMode,
  isTemplate,
}: {
  anchorElem?: HTMLElement;
  setIsLinkEditMode: (isLinkEditMode: boolean) => void;
  isTemplate?: boolean;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingTextFormatToolbar(
    editor,
    anchorElem,
    setIsLinkEditMode,
    isTemplate
  );
}
